import { ChangeEvent, useState } from 'react';
import { Alert } from '@mui/material';
import {
  CoreBox,
  CoreDialogContent,
  CoreFormControlLabel,
  CoreRadio,
  CoreSwitch,
  CoreTypography,
} from '@youscience/core';

import { isEmpty, isUndefined } from 'lodash-es';

import { SchoolSurveyData } from '@interfaces/favoriteSurvey';
import { InitialModalsStatus } from '@interfaces/optIn';

import DialogFooter from '@components/DialogFooter';

import useOptInStore from '@store/OptInStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';

import { sxStyles, StyledDialog, StyledRadioGroup } from './OptInConnection.styles';

interface OptInConnectionProps {
  schoolInfo: SchoolSurveyData;
  isOpenOptInConnection: boolean;
  setActiveModal: (field?: keyof InitialModalsStatus) => void;
}

export const OptInConnection = (props: OptInConnectionProps) => {
  const {
    schoolInfo: { id, name },
    isOpenOptInConnection,
    setActiveModal,
  } = props;

  const {
    userPreferences,
    userPreferences: { personalInfo },
    saveUserSharingPreferences,
  } = useUserPreferencesStore((state) => state);

  const {
    optInConnectionData: { share },
    saveUserConnection,
    setOptInConnectionData,
    resetOptInConnectionData,
  } = useOptInStore((state) => state);

  const [isNotToBeAskedToShare, setIsNotToBeAskedToShare] = useState(userPreferences.isNotToBeAskedToShare ?? false);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptInConnectionData({ share: !!event.target.value });

    if (event.target.value) {
      setIsNotToBeAskedToShare(false);
    }
  };

  const handleCheckBoxChange = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsNotToBeAskedToShare(checked);
  };

  const handleOpenShareInfo = () => {
    setActiveModal('shareInfo');
  };

  const handleSkipOptInConnection = () => {
    setActiveModal('successDialog');
    resetOptInConnectionData();

    saveUserConnection(id, false);
  };

  const handleSaveOptInConnection = async () => {
    await saveUserSharingPreferences({ isNotToBeAskedToShare });

    if (!isNotToBeAskedToShare) {
      saveUserConnection(id, share ?? false);
    }

    if (share && isEmpty(personalInfo)) {
      setActiveModal('optInContactForm');
    } else {
      setActiveModal('successDialog');
    }
  };

  return (
    <StyledDialog data-testid="optInConnectionDialog" open={isOpenOptInConnection}>
      <CoreDialogContent sx={sxStyles.content}>
        <CoreBox sx={sxStyles.titles}>
          <CoreTypography data-testid="title" variant="h4" component="h3">{`Connect with ${name}`}</CoreTypography>

          <CoreTypography data-testid="subtitle">
            {`Would you like us to share your information with ${name}`}
            <br />
            so they can contact you about their programs?
          </CoreTypography>
        </CoreBox>

        <CoreBox sx={sxStyles.formWrap}>
          <StyledRadioGroup onChange={handleRadioChange}>
            <CoreFormControlLabel
              sx={{ alignItems: 'center' }}
              control={<CoreRadio data-testid="optInShareRadio" checked={share} value="Yes" />}
              label={<CoreBox>Yes! Please share my information with {name}.</CoreBox>}
            />

            {share && (
              <Alert data-testid="infoMessage" sx={sxStyles.alert} severity="info">
                Please note that we will notify you once we have shared your information with the school, and that
                sharing your information with the school does not guarantee they will contact you. Remember to always
                double-check the college admission website for the most up-to-date information on application deadlines,
                campus visits, and financial aid.
              </Alert>
            )}

            <CoreFormControlLabel
              sx={{ alignItems: 'center' }}
              control={<CoreRadio data-testid="optInNotShareRadio" checked={share === false} value="" />}
              label={<CoreBox>No, I don&apos;t want to be connected with {name}.</CoreBox>}
            />
          </StyledRadioGroup>

          {!isUndefined(share) && !share && (
            <CoreFormControlLabel
              sx={{ alignItems: 'center' }}
              control={
                <CoreSwitch
                  data-testid="isNotToBeAskedToShare"
                  checked={isNotToBeAskedToShare}
                  onChange={handleCheckBoxChange}
                />
              }
              label="I don't want to be connected with any schools at this time."
            />
          )}
        </CoreBox>

        <CoreTypography data-testid="shareInfoText" sx={sxStyles.shareInfo} component="div" variant="caption">
          To see a list of the information we may share,{' '}
          <CoreTypography data-testid="shareInfoBtn" variant="caption" onClick={handleOpenShareInfo}>
            click here
          </CoreTypography>
          . Participating schools may compensate YouScience for this service, but there is no cost to you. You can opt
          out of sharing at any time by visiting your profile menu.
        </CoreTypography>
      </CoreDialogContent>

      <DialogFooter
        goBackButtonTitle="Skip for now"
        goNextButtonTitle="Save"
        goBackActionButtonTitle="Saving"
        isDisableNextButton={isUndefined(share)}
        onGoBack={handleSkipOptInConnection}
        onGoNext={handleSaveOptInConnection}
      />
    </StyledDialog>
  );
};
